import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { GlobalStore } from '@highlight/ipc'
import { useAuth } from '@renderer/chat/providers'
import { registerBeforeAudioNoteSave, trackBeforeAudioNoteSave } from '@renderer/ipc/before-audio-note-save'
import { meetingNotepadModeAtom, setMeetingNotepadSizeAtom, type MeetingNotepadMode } from './atoms'
import { fetchSavedNotepad, fetchSessionNotepad, pushDocument, pushSessionNotepad } from './meeting-notepad-api'

/** Typing pause before notes reach disk (and the server). Long enough that a
 *  normal sentence is one save, short enough that a hard quit can't cost a
 *  thought. */
const NOTEPAD_SAVE_DEBOUNCE_MS = 500

/**
 * Notes live on disk beside the other `meeting.*` entries, so a meeting
 * reopened tomorrow still shows what was written in it. The same markdown is
 * the server document (HIG-5815) — the session's Durable Object while live,
 * the audio note after save.
 */
const notepadStore = () => GlobalStore.conversations()
const notepadStoreKey = (key: string) => `meeting.${key}.notepad`

/** Server ids for the meeting whose notepad is being opened. */
export type MeetingNotepadServerIds = Pick<MeetingNotepadMode, 'audioSessionId' | 'audioNoteId'>

/** Footer-mode toggle for the notepad buttons (Meeting View and floating bar). */
export function useMeetingNotepadToggle(key: string, serverIds?: MeetingNotepadServerIds) {
  const mode = useAtomValue(meetingNotepadModeAtom)
  const setMode = useSetAtom(meetingNotepadModeAtom)

  // Opening builds a fresh mode, so a reopen always lands at its resting size
  // whatever the last one was left on — no separate reset to keep in step.
  const toggle = useCallback(
    () => setMode((current) => (current?.key === key ? null : { key, size: 'normal', ...serverIds })),
    [key, serverIds?.audioSessionId, serverIds?.audioNoteId, setMode], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return { isOpen: mode?.key === key, toggle }
}

/**
 * Notepad content and disclosure for the meeting holding the footer.
 *
 * Notes go straight to {@link GlobalStore} rather than through a jotai atom.
 * `atomWithGlobalStore` is `atomWithStorage`, which only loads from storage in
 * `onMount` — nothing else subscribes to a notepad atom, so it would sit on its
 * empty initial value, hand the textarea a blank note, and write that emptiness
 * back over what was saved. A direct `get` is correct whenever it's called.
 *
 * One key per meeting, `meeting.<key>.notepad`, alongside the existing
 * `meeting.<id>.preparation` entries. Per-key rather than one map so a save for
 * one meeting can't read-modify-write another's notes out of existence.
 *
 * Typing is held for {@link NOTEPAD_SAVE_DEBOUNCE_MS} before it reaches the
 * store, because every write is an encrypted save fanned out to every window —
 * a keystroke is far too often. The pending value is flushed when the notepad
 * closes, when it moves to another meeting, and on unmount, so nothing is owed
 * at any point the user can navigate away.
 *
 * Each flush also pushes the markdown to the server document. Any later
 * staged-action run reads the latest live-session document; reopened notes
 * write the same document through its owner-only document ID.
 */
export function useMeetingNotepadContent() {
  const mode = useAtomValue(meetingNotepadModeAtom)
  const setMode = useSetAtom(meetingNotepadModeAtom)
  const setSize = useSetAtom(setMeetingNotepadSizeAtom)
  const key = mode?.key
  const document = useMeetingNotepadDocument(key, mode ?? undefined)

  // Collapse and full height are two independent trips away from the resting
  // size, so each toggle returns to `normal` rather than to the other one.
  // Pressing full height on a collapsed notepad opens it full, and closing that
  // lands back on the panel the user knows.
  const size = mode?.size ?? 'normal'
  const toggleCollapsed = useCallback(() => setSize(size === 'collapsed' ? 'normal' : 'collapsed'), [size, setSize])

  const toggleFull = useCallback(() => setSize(size === 'full' ? 'normal' : 'full'), [size, setSize])

  /** Give the slot back, the same dismissal the floating bar's toggle makes. */
  const close = useCallback(() => setMode(null), [setMode])

  return {
    key,
    ...document,
    isCollapsed: size === 'collapsed',
    isFull: size === 'full',
    toggleCollapsed,
    toggleFull,
    close,
  }
}

/**
 * One meeting's notes, wherever they're edited: the footer notepad, or the
 * meeting's Notes tab. See {@link useMeetingNotepadContent} for where they're
 * stored and when they flush.
 */
export function useMeetingNotepadDocument(key: string | undefined, serverIds?: MeetingNotepadServerIds) {
  const { getAuthToken } = useAuth()
  const audioSessionId = serverIds?.audioSessionId
  const audioNoteId = serverIds?.audioNoteId

  const initialNotes = useMemo(() => (key ? notepadStore().get<string>(notepadStoreKey(key), '') : ''), [key])

  // The server copy of this meeting's notepad document — live session first,
  // saved note as the fallback for reopened meetings. It only ever seeds an
  // empty editor (see notepad.tsx), so a failure or slow fetch costs nothing:
  // the local copy is already showing.
  const { data: serverNotepad } = useQuery({
    queryKey: ['meeting-notepad-server', audioSessionId ?? audioNoteId],
    queryFn: async () => {
      if (audioSessionId) {
        return await fetchSessionNotepad(getAuthToken, audioSessionId)
      }
      if (audioNoteId) {
        return await fetchSavedNotepad(getAuthToken, audioNoteId)
      }
      return null
    },
    enabled: !!(audioSessionId ?? audioNoteId),
    staleTime: 60 * 1000,
  })

  const documentId =
    serverNotepad && 'documentId' in serverNotepad && typeof serverNotepad.documentId === 'string'
      ? serverNotepad.documentId
      : undefined
  const pendingRef = useRef<{
    key: string
    markdown: string
    audioSessionId?: string
    documentId?: string
  } | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const serverWriteRef = useRef(Promise.resolve())

  const flushNotes = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const pending = pendingRef.current
    if (!pending) return
    pendingRef.current = null
    notepadStore().setIfChanged(notepadStoreKey(pending.key), pending.markdown)
    let push: (() => Promise<void>) | undefined
    if (pending.audioSessionId) {
      const sessionId = pending.audioSessionId
      push = () => pushSessionNotepad(getAuthToken, sessionId, pending.markdown)
    } else if (pending.documentId) {
      const savedDocumentId = pending.documentId
      push = () => pushDocument(getAuthToken, savedDocumentId, pending.markdown)
    }
    if (push) {
      const write = serverWriteRef.current.then(push)
      serverWriteRef.current = write
      await trackBeforeAudioNoteSave(write)
    }
  }, [getAuthToken])

  const updateNotes = useCallback(
    (markdown: string) => {
      if (!key) return
      // Keyed with the value: a flush that lands after the notepad has moved on
      // must still write the meeting the text was typed into. The session id is
      // captured the same way, so the server write can't cross meetings either.
      pendingRef.current = { key, markdown, audioSessionId, documentId }
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => void flushNotes(), NOTEPAD_SAVE_DEBOUNCE_MS)
    },
    [key, audioSessionId, documentId, flushNotes],
  )

  useEffect(() => {
    if (!audioSessionId) return
    return registerBeforeAudioNoteSave(flushNotes)
  }, [audioSessionId, flushNotes])

  // Closing the notepad or switching meetings settles the previous key's text
  // before the editor remounts on the new one.
  useEffect(
    () => () => {
      void flushNotes()
    },
    [key, flushNotes],
  )

  return {
    initialNotes,
    serverMarkdown: serverNotepad?.markdown ?? null,
    updateNotes,
  }
}
